import { debounce } from '@bjb/bjb-web-components/dist/assets/scripts/JB';

/**
 * Resolve the element a URL fragment points at.
 *
 * The fragment is treated as an element id (native anchor semantics), not as a CSS selector: a
 * parameter-style hash such as `#overlayNews=7874` is a valid fragment but an invalid selector, so
 * feeding it to `querySelector` would throw a DOMException. `getElementById` never throws and simply
 * returns `null` when no element matches, which lets non-anchor hashes (e.g. the news overlay deep
 * link) pass through harmlessly.
 */
export function getAnchorTarget(hash: string): HTMLElement | null {
  const rawId = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!rawId) {
    return null;
  }

  let id = rawId;
  try {
    id = decodeURIComponent(rawId);
  } catch {
    // Malformed percent-encoding: fall back to the raw fragment rather than throwing.
  }

  return document.getElementById(id);
}

async function onLoad() {
  if (!window.location.hash) {
    return;
  }

  const debouncedScroll = debounce(() => {
    const element = getAnchorTarget(window.location.hash);

    element?.scrollIntoView();
    resizeObserver.disconnect();
  }, 150);

  const resizeObserver = new ResizeObserver(() => {
    debouncedScroll();
  });

  resizeObserver.observe(document.body);
}

if (document.readyState === 'complete') {
  onLoad();
} else {
  window.addEventListener('load', onLoad, { once: true });
}
